import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const TONES = {
  success: { icon: CheckCircle2, wrapper: 'border-green-200 bg-green-50 text-green-900', icon_color: 'text-green-600' },
  error: { icon: AlertCircle, wrapper: 'border-red-200 bg-red-50 text-red-900', icon_color: 'text-red-600' },
  info: { icon: Info, wrapper: 'border-blue-200 bg-blue-50 text-blue-900', icon_color: 'text-blue-600' }
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(({ type = 'info', title, message, duration = 5000 }) => {
    counter.current += 1;
    const id = counter.current;
    setToasts((current) => [...current, { id, type, title, message }]);
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const api = useMemo(() => ({
    show,
    dismiss,
    success: (message, options = {}) => show({ type: 'success', message, ...options }),
    error: (message, options = {}) => show({ type: 'error', message, duration: 8000, ...options }),
    info: (message, options = {}) => show({ type: 'info', message, ...options })
  }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 px-4 pb-4 sm:items-end sm:px-6"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        {toasts.map((toast) => {
          const tone = TONES[toast.type] || TONES.info;
          const Icon = tone.icon;
          return (
            <div
              key={toast.id}
              role={toast.type === 'error' ? 'alert' : 'status'}
              className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border p-4 shadow-lg ${tone.wrapper}`}
            >
              <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${tone.icon_color}`} aria-hidden="true" />
              <div className="min-w-0 flex-1 text-sm">
                {toast.title && <p className="font-semibold">{toast.title}</p>}
                <p className={toast.title ? 'mt-0.5' : ''}>{toast.message}</p>
              </div>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="-m-1 rounded p-1 text-current opacity-60 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
                aria-label="Dismiss notification"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    // Allow components to render outside a provider (tests, storybook) without crashing.
    return {
      show: () => null,
      dismiss: () => null,
      success: () => null,
      error: () => null,
      info: () => null
    };
  }
  return context;
};

export default ToastProvider;
